/**
 * useExpanderState — Persistent collapsible section state.
 * Stores open/closed state in localStorage so sections remember their position across page loads.
 * Falls back to plain useState when no key is provided (backward compatible).
 */
import { useState, useCallback } from "react";
import { makeKeyedStore, scopedKey } from "../utils";

const store = makeKeyedStore("sendly-expanders");

/**
 * @param {string|null} key — Unique persist key (e.g. "admin.branding"). Null = no persistence.
 * @param {boolean} defaultOpen — Default open state when no stored value exists.
 * @param {string|null} scope — Optional scope prefix (e.g. gymId). Produces "gymId:key" storage keys.
 * @returns {[boolean, function, function]} — [isOpen, toggle, setOpen]
 */
export function useExpanderState(key, defaultOpen = true, scope = null) {
  const fullKey = scopedKey(key, scope);

  const [open, setOpenRaw] = useState(() => {
    if (!fullKey) return defaultOpen;
    const all = store.read();
    return fullKey in all ? all[fullKey] : defaultOpen;
  });

  const persist = useCallback((value) => {
    if (!fullKey) return;
    const all = store.read();
    all[fullKey] = value;
    store.write(all);
  }, [fullKey]);

  const toggle = useCallback(() => {
    setOpenRaw(prev => {
      const next = !prev;
      persist(next);
      return next;
    });
  }, [persist]);

  const setOpen = useCallback((value) => {
    const v = typeof value === "function" ? value(open) : value;
    setOpenRaw(v);
    persist(v);
  }, [open, persist]);

  return [open, toggle, setOpen];
}
